/**
 * Condition Engine
 * Handles conditional field/area visibility
 */

import { ConditionEvaluator } from './condition_evaluator.js';

export class ConditionEngine {
  constructor(container = null) {
    this.container = container || document;
    this.conditionListeners = new Map();
    /** Monitored field id -> [{ controlledFieldId, conditions }] for event delegation */
    this.fieldConditionsMap = new Map();
    /** Flat list of all conditional fields so we can re-evaluate every one on any change (avoids map misses) */
    this.allFieldConditionEntries = [];
    this._fieldDelegationBound = false;
  }

  /**
   * Setup condition listeners for area
   * @param {string} areaId - Area ID
   * @param {Array} conditions - Condition blocks
   */
  setupAreaConditions(areaId, conditions) {
    if (!conditions || !Array.isArray(conditions) || conditions.length === 0) {
      return;
    }

    // Get all fields referenced in conditions
    const fieldIds = new Set();
    conditions.forEach(block => {
      if (block.terms && Array.isArray(block.terms)) {
        block.terms.forEach(term => {
          if (term.field) {
            // Handle FUNC_ prefix
            let fieldId = term.field;
            if (fieldId.indexOf('FUNC_') > -1) {
              const parts = fieldId.split('_');
              fieldId = parts[2] || fieldId;
            }
            fieldIds.add(fieldId);
          }
        });
      }
    });

    // Check if area element exists (scope to form container)
    const areaElement = this.container.querySelector(`.area[data-id="${areaId}"]`);

    // Setup listeners for each field (scope to form container), including repeater inputs
    fieldIds.forEach(fieldId => {
      const exactFields = this.container.querySelectorAll(`[name="${fieldId}"], [name="${fieldId}[]"]`);
      const repeaterFields = this.container.querySelectorAll(`[name^="${fieldId}["]`);
      const seen = new Set();
      const allFields = [];
      exactFields.forEach(f => {
        if (!seen.has(f)) { seen.add(f); allFields.push(f); }
      });
      repeaterFields.forEach(f => {
        if (!seen.has(f)) { seen.add(f); allFields.push(f); }
      });
      
      allFields.forEach(field => {
        const handler = () => {
          this.evaluateAreaConditions(areaId, conditions);
        };
        
        field.addEventListener('input', handler);
        field.addEventListener('change', handler);
        
        // Store listener for cleanup
        if (!this.conditionListeners.has(areaId)) {
          this.conditionListeners.set(areaId, []);
        }
        this.conditionListeners.get(areaId).push({ field, handler, event: 'input' });
        this.conditionListeners.get(areaId).push({ field, handler, event: 'change' });
      });
    });

    // Initial evaluation
    this.evaluateAreaConditions(areaId, conditions);
  }

  /**
   * Evaluate conditions for area
   * @param {string} areaId - Area ID
   * @param {Array} conditions - Condition blocks
   */
  evaluateAreaConditions(areaId, conditions) {
    const matched = ConditionEvaluator.evaluateBlocks(conditions, this.container, { controlledAreaId: areaId });
    const area = this.container.querySelector(`.area[data-id="${areaId}"]`);
    
    if (!area) {
      return;
    }
    
    if (matched) {
      // Show area
      area.classList.remove('d-none');
      area.classList.add('d-block');
      
      // Restore required attributes (skip checkboxes: multi-checkbox groups use "at least one" in JS only)
      const inputs = area.querySelectorAll('input[data-required="required"]');
      inputs.forEach(input => {
        if (input.type !== 'checkbox') {
          input.setAttribute('required', 'required');
        }
      });
      
      const selects = area.querySelectorAll('select[data-required="required"]');
      selects.forEach(select => {
        select.setAttribute('required', 'required');
      });
    } else {
      // Hide area
      area.classList.remove('d-block');
      area.classList.add('d-none');
      
      // Remove required attributes
      const inputs = area.querySelectorAll('input[required]');
      inputs.forEach(input => {
        input.removeAttribute('required');
      });
      
      const selects = area.querySelectorAll('select[required]');
      selects.forEach(select => {
        select.removeAttribute('required');
      });
    }
  }

  /**
   * Get base field name from input name (strip repeater index e.g. "id[0]" -> "id")
   */
  _baseFieldName(name) {
    if (!name || typeof name !== 'string') return '';
    const idx = name.indexOf('[');
    return idx > -1 ? name.slice(0, idx) : name;
  }

  /**
   * Check whether any condition term references the given field (by baseName or raw name).
   */
  _conditionReferencesField(conditions, baseName, rawName) {
    if (!conditions || !Array.isArray(conditions)) return false;
    for (let b = 0; b < conditions.length; b++) {
      const block = conditions[b];
      if (!block.terms || !Array.isArray(block.terms)) continue;
      for (let t = 0; t < block.terms.length; t++) {
        const term = block.terms[t];
        if (!term.field) continue;
        let termField = String(term.field).trim();
        if (termField.indexOf('FUNC_') > -1) {
          const parts = termField.split('_');
          termField = parts[2] || termField;
        }
        const termBase = this._baseFieldName(termField) || termField;
        if (termBase === baseName || termField === baseName || termBase === rawName || termField === rawName) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Bind delegated change/input listener once for all field conditions
   */
  _bindFieldDelegation() {
    if (this._fieldDelegationBound || !this.container) return;
    this._fieldDelegationBound = true;
    const handler = (e) => {
      if (e && e.isTrusted === false && !e.tqProgrammaticFill && !e.tqUserSelect) return;
      if (this._delegationHandling) return;
      let el = e.target;
      if (!el) return;
      if (el.tagName === 'LABEL') {
        el = el.control != null ? el.control : (el.htmlFor && this.container ? this.container.querySelector(`#${CSS.escape(el.htmlFor)}`) : null) || el.querySelector('input, select, textarea');
        if (!el) return;
      }
      if (el.tagName !== 'INPUT' && el.tagName !== 'SELECT' && el.tagName !== 'TEXTAREA') return;
      const name = el.getAttribute('name');
      const baseName = this._baseFieldName(name);
      if (!baseName) return;
      const rowScope = el.closest('.colsListWrapper');
      this._delegationHandling = true;
      try {
        this.allFieldConditionEntries.forEach(({ controlledFieldId, conditions }) => {
          if (!this._conditionReferencesField(conditions, baseName, name)) return;
          this.evaluateFieldConditions(controlledFieldId, conditions, rowScope);
        });
      } finally {
        this._delegationHandling = false;
      }
    };
    this.container.addEventListener('input', handler, true);
    this.container.addEventListener('change', handler, true);
    if (!this.conditionListeners.has('_delegation')) {
      this.conditionListeners.set('_delegation', []);
    }
    this.conditionListeners.get('_delegation').push({ field: this.container, handler, event: 'input' });
    this.conditionListeners.get('_delegation').push({ field: this.container, handler, event: 'change' });
  }

  /**
   * Setup condition listeners for field (uses event delegation so inputs need not exist yet)
   * @param {string} fieldId - Field ID (the field whose visibility is controlled)
   * @param {Array} conditions - Condition blocks
   */
  setupFieldConditions(fieldId, conditions) {
    if (!conditions || !Array.isArray(conditions) || conditions.length === 0) {
      return;
    }

    const controlledFieldId = fieldId;
    const monitoredIds = new Set();
    conditions.forEach(block => {
      if (block.terms && Array.isArray(block.terms)) {
        block.terms.forEach(term => {
          if (term.field) {
            let mid = String(term.field).trim();
            if (!mid) return;
            if (mid.indexOf('FUNC_') > -1) {
              const parts = mid.split('_');
              mid = parts[2] || mid;
            }
            const base = this._baseFieldName(mid) || mid;
            monitoredIds.add(base);
            if (base !== mid) monitoredIds.add(mid);
          }
        });
      }
    });

    monitoredIds.forEach(monitoredFieldId => {
      if (!this.fieldConditionsMap.has(monitoredFieldId)) {
        this.fieldConditionsMap.set(monitoredFieldId, []);
      }
      const list = this.fieldConditionsMap.get(monitoredFieldId);
      if (!list.some(entry => entry.controlledFieldId === controlledFieldId)) {
        list.push({ controlledFieldId, conditions });
      }
    });

    if (!this.allFieldConditionEntries.some(e => e.controlledFieldId === controlledFieldId)) {
      this.allFieldConditionEntries.push({ controlledFieldId, conditions });
    }

    this._bindFieldDelegation();

    this.evaluateFieldConditions(fieldId, conditions);
  }

  /**
   * Evaluate conditions for field (optionally scoped to a repeater row)
   * @param {string} fieldId - Field ID
   * @param {Array} conditions - Condition blocks
   * @param {Element|null} [rowScope] - Optional repeater row (.colsListWrapper) to scope evaluation and visibility
   */
  evaluateFieldConditions(fieldId, conditions, rowScope = null) {
    if (rowScope) {
      let fieldContainer = rowScope.querySelector(`.formCol[data-id="${fieldId}"]`) || rowScope.querySelector(`[data-id="${fieldId}"]`);
      if (fieldContainer) {
        // Field IS inside this row: evaluate using row values
        const matched = ConditionEvaluator.evaluateBlocks(conditions, rowScope, { controlledFieldId: fieldId });
        this._applyFieldVisibility(fieldContainer, fieldId, matched);
      } else {
        // Field is NOT in this row: evaluate using form container (global values)
        fieldContainer = this.container.querySelector(`.formCol[data-id="${fieldId}"]`) || this.container.querySelector(`[data-id="${fieldId}"]`);
        if (!fieldContainer) return;
        const matched = ConditionEvaluator.evaluateBlocks(conditions, this.container, { controlledFieldId: fieldId });
        this._applyFieldVisibility(fieldContainer, fieldId, matched);
      }
      return;
    }

    const allCols = this.container.querySelectorAll(`.formCol[data-id="${fieldId}"]`);
    if (allCols.length === 0) {
      const fallback = this.container.querySelector(`[data-id="${fieldId}"]`);
      if (fallback) {
        const matched = ConditionEvaluator.evaluateBlocks(conditions, this.container, { controlledFieldId: fieldId });
        this._applyFieldVisibility(fallback, fieldId, matched);
      }
      return;
    }

    allCols.forEach((fieldContainer) => {
      const row = fieldContainer.closest('.colsListWrapper');
      if (row) {
        this.evaluateFieldConditions(fieldId, conditions, row);
      } else {
        const matched = ConditionEvaluator.evaluateBlocks(conditions, this.container, { controlledFieldId: fieldId });
        this._applyFieldVisibility(fieldContainer, fieldId, matched);
      }
    });
  }

  /**
   * Apply show/hide to a field container based on condition match
   * @param {Element} fieldContainer - .formCol or element with data-id
   * @param {string} fieldId - Field ID (for validation)
   * @param {boolean} matched - Whether conditions matched
   */
  _applyFieldVisibility(fieldContainer, fieldId, matched) {
    const containerDataId = fieldContainer.getAttribute('data-id');
    if (containerDataId !== fieldId) return;
    const isFormCol = fieldContainer.classList.contains('formCol');
    const hasFieldContent = fieldContainer.querySelector(`[id="${fieldId}"], [name="${fieldId}"], [name="${fieldId}[]"], [name^="${fieldId}["]`) !== null;
    if (!isFormCol && !hasFieldContent) return;

    const wasHidden = fieldContainer.classList.contains('d-none') ||
      (fieldContainer.querySelector('.form-group') && fieldContainer.querySelector('.form-group').classList.contains('d-none'));

    if (matched) {
      const formGroup = fieldContainer.querySelector('.form-group');
      if (formGroup) {
        formGroup.classList.remove('d-none');
      } else {
        fieldContainer.classList.remove('d-none');
      }
      const requiredElements = fieldContainer.querySelectorAll('input[data-required="required"], select[data-required="required"], textarea[data-required="required"]');
      requiredElements.forEach((element) => {
        if (element.type !== 'checkbox') {
          element.setAttribute('required', 'required');
        }
      });
    } else {
      const formGroup = fieldContainer.querySelector('.form-group');
      if (formGroup) {
        formGroup.classList.add('d-none');
      } else {
        fieldContainer.classList.add('d-none');
      }
      const requiredElements = fieldContainer.querySelectorAll('input[required], select[required], textarea[required]');
      requiredElements.forEach((element) => {
        element.removeAttribute('required');
      });
    }

    const isNowHidden = !matched;
    if (wasHidden !== isNowHidden) {
      this._cascadeFieldVisibility(fieldId);
      if (this.container) {
        this.container.dispatchEvent(new CustomEvent('form.fieldVisibilityChanged', { bubbles: true }));
      }
    }
  }

  /**
   * After a field's visibility changes, re-evaluate any conditional fields that depend on it.
   */
  _cascadeFieldVisibility(changedFieldId) {
    if (this._cascading) return;
    this._cascading = true;
    try {
      this.allFieldConditionEntries.forEach(({ controlledFieldId, conditions }) => {
        if (controlledFieldId === changedFieldId) return;
        if (this._conditionReferencesField(conditions, changedFieldId, changedFieldId)) {
          this.evaluateFieldConditions(controlledFieldId, conditions);
        }
      });
    } finally {
      this._cascading = false;
    }
  }

  /**
   * Cleanup condition listeners
   */
  cleanup() {
    this.conditionListeners.forEach((listeners) => {
      listeners.forEach((listener) => {
        if (listener.field && listener.handler && listener.event) {
          listener.field.removeEventListener(listener.event, listener.handler);
        }
      });
    });
    this.conditionListeners.clear();
    this.fieldConditionsMap.clear();
    this.allFieldConditionEntries = [];
    this._fieldDelegationBound = false;
  }
}